import { useRef } from 'react';

import { useInViewport } from 'ahooks';

import {
  Box,
  Card,
  FormHelp,
  Heading,
  Paragraph,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';

import CashFlowField from './CashFlowField';
import { calculateCashFlow, formatCurrency, formatPercent } from './cashFlow';
import useCashFlowUrlState from './useCashFlowUrlState';

import type { CashFlowErrors, CashFlowFieldName, CashFlowResult, CashFlowValues, PurchaseMethod } from './cashFlow';

const DEFAULT_VALUES: CashFlowValues = {
  purchasePrice: '100,000',
  initialRepairs: '0',
  closingCosts: '3,000',
  downPaymentPercent: '25',
  interestRate: '5',
  loanTermYears: '30',
  lenderFees: '0',
  monthlyRent: '1,200',
  otherIncome: '0',
  vacancyPercent: '5',
  annualTaxes: '1,800',
  annualInsurance: '1,200',
  monthlyHoa: '0',
  monthlyUtilities: '0',
  maintenancePercent: '5',
  managementPercent: '8',
  otherExpenses: '0',
};

const PROPERTY_FIELDS: Array<FieldConfig> = [
  { field: 'purchasePrice', label: 'Purchase price', unit: '$' },
  { field: 'initialRepairs', label: 'Initial repairs', unit: '$' },
  { field: 'closingCosts', label: 'Closing costs', unit: '$' },
];

const FINANCING_FIELDS: Array<FieldConfig> = [
  { field: 'downPaymentPercent', label: 'Down payment (%)', unit: '%' },
  { field: 'interestRate', label: 'Annual interest rate (%)', unit: '%' },
  { field: 'loanTermYears', label: 'Loan term (years)', unit: 'years' },
  { field: 'lenderFees', label: 'Other lender fees', unit: '$' },
];

const INCOME_FIELDS: Array<FieldConfig> = [
  { field: 'monthlyRent', label: 'Monthly rent', unit: '$' },
  { field: 'otherIncome', label: 'Other monthly income', unit: '$' },
  { field: 'vacancyPercent', label: 'Vacancy allowance (%)', unit: '%' },
];

const OPERATING_FIELDS: Array<FieldConfig> = [
  { field: 'annualTaxes', label: 'Annual property taxes', unit: '$' },
  { field: 'annualInsurance', label: 'Annual insurance', unit: '$' },
  { field: 'monthlyHoa', label: 'Monthly HOA fees', unit: '$' },
  { field: 'monthlyUtilities', label: 'Monthly owner-paid utilities', unit: '$' },
  { field: 'maintenancePercent', label: 'Maintenance reserve (% of rent)', unit: '%' },
  { field: 'managementPercent', label: 'Management fee (% of collected rent)', unit: '%' },
  { field: 'otherExpenses', label: 'Other monthly expenses', unit: '$' },
];

const FIELD_GRID_CSS = {
  display: 'grid',
  gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
  columnGap: '400',
  rowGap: 'zero',
  marginBlockEnd: '-400',
} as const;

const COLUMN_CSS = { display: 'flex', flexDirection: 'column', gap: '400' } as const;

const RESULTS_COLUMN_CSS = {
  display: 'flex',
  flexDirection: 'column',
  gap: '400',
  alignSelf: { lg: 'stretch' },
} as const;

const ESTIMATE_CARD_CSS = {
  position: { lg: 'sticky' },
  top: { lg: '400' },
} as const;

const PINNED_SUMMARY_ANCHOR_CSS = {
  position: 'sticky',
  bottom: 'zero',
  zIndex: '2',
  display: { base: 'block', lg: 'none' },
  height: 'zero',
  marginBlockStart: '-400',
} as const;

const PINNED_SUMMARY_CSS = {
  position: 'absolute',
  insetInline: 'zero',
  bottom: '200',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '400',
  '&[data-hidden]': { visibility: 'hidden' },
} as const;

const getSummaryRows = (result: CashFlowResult, purchaseMethod: PurchaseMethod): Array<SummaryRow> => [
  { label: 'Income after vacancy / month', value: formatCurrency(result.incomeAfterVacancy) },
  { label: 'Operating costs / month', value: formatCurrency(result.operatingCosts) },
  { label: 'Net operating income / month', value: formatCurrency(result.netOperatingIncome) },
  ...(purchaseMethod === 'mortgage'
    ? [{ label: 'Mortgage principal & interest / month', value: formatCurrency(result.mortgagePayment) }]
    : []),
  { label: 'Annual cash flow', value: formatCurrency(result.annualCashFlow) },
  { label: 'Initial cash invested', value: formatCurrency(result.cashInvested) },
];

type FieldConfig = {
  field: CashFlowFieldName;
  label: string;
  unit: '$' | '%' | 'years';
};

type SummaryRow = {
  label: string;
  value: string;
};

const CashFlowCalculator = () => {
  const { values, setValues, purchaseMethod, setPurchaseMethod } = useCashFlowUrlState(DEFAULT_VALUES);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isResultsInView] = useInViewport(resultsRef);

  const calculation = calculateCashFlow(values, purchaseMethod);
  const errors: CashFlowErrors = calculation.ok ? {} : calculation.errors;

  const renderFields = (fields: Array<FieldConfig>) => (
    <Box css={FIELD_GRID_CSS}>
      {fields.map(({ field, label, unit }) => (
        <CashFlowField
          key={field}
          label={label}
          unit={unit}
          value={values[field]}
          error={errors[field]}
          onChange={(value) => setValues((previous) => ({ ...previous, [field]: value }))}
        />
      ))}
    </Box>
  );

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', width: '100%' }}>
      <Heading level={2} textStyle="heading-md">
        Rental cash flow calculator
      </Heading>

      <Paragraph textStyle="body-sm">
        Example numbers are a starting point, not market estimates. Results update as you edit.
      </Paragraph>

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr', lg: '2fr 1fr' },
          gap: '400',
          alignItems: 'start',
        }}
      >
        <Box css={COLUMN_CSS}>
          <Card elevated={false}>
            <Heading level={3} textStyle="heading-sm">
              Purchase and financing
            </Heading>

            <Paragraph textStyle="body-sm" css={{ marginBlockEnd: '400' }}>
              Include the upfront costs needed to get your property ready to rent.
            </Paragraph>

            <Text textStyle="body-bold">Property costs</Text>

            {renderFields(PROPERTY_FIELDS)}

            <Box css={{ marginBlock: '400' }}>
              <Text textStyle="body-bold">Purchase method</Text>

              <ToggleButtonGroup
                value={purchaseMethod}
                onValueChange={(value) => {
                  if (value === 'mortgage' || value === 'cash') {
                    setPurchaseMethod(value);
                  }
                }}
                aria-label="Purchase method"
                css={{ marginBlockStart: '200' }}
              >
                <ToggleButton value="mortgage">Mortgage</ToggleButton>
                <ToggleButton value="cash">Cash purchase</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {purchaseMethod === 'mortgage' && renderFields(FINANCING_FIELDS)}
          </Card>

          <Card elevated={false}>
            <Heading level={3} textStyle="heading-sm">
              Income and expenses
            </Heading>

            <Paragraph textStyle="body-sm" css={{ marginBlockEnd: '400' }}>
              Use total income and costs for the entire property. Include only expenses you pay as the owner.
            </Paragraph>

            <Text textStyle="body-bold">Rental income</Text>

            {renderFields(INCOME_FIELDS)}

            <FormHelp css={{ marginBlockStart: '200', marginBlockEnd: '400' }}>
              Vacancy reduces scheduled rent, not other income. Other income may include parking, storage, or laundry
              fees.
            </FormHelp>

            <Text textStyle="body-bold">Operating costs</Text>

            {renderFields(OPERATING_FIELDS)}

            <FormHelp css={{ marginBlockStart: '200' }}>
              Maintenance is based on rent before vacancy. Management fees use rent after vacancy. Annual taxes and
              insurance are divided by 12. Add replacement reserves or other recurring costs under other monthly
              expenses.
            </FormHelp>
          </Card>

          <Box aria-hidden css={PINNED_SUMMARY_ANCHOR_CSS}>
            <Card data-hidden={isResultsInView || undefined} css={PINNED_SUMMARY_CSS}>
              {calculation.ok ? (
                <>
                  <Box>
                    <Text textStyle="body-sm">Monthly cash flow</Text>
                    <Text textStyle="heading-sm" css={{ display: 'block' }}>
                      {formatCurrency(calculation.result.monthlyCashFlow)}
                    </Text>
                  </Box>

                  <Box>
                    <Text textStyle="body-sm">Cash-on-cash return</Text>
                    <Text textStyle="heading-sm" css={{ display: 'block' }}>
                      {calculation.result.cashOnCashReturn === undefined
                        ? 'N/A'
                        : formatPercent(calculation.result.cashOnCashReturn)}
                    </Text>
                  </Box>
                </>
              ) : (
                <Text textStyle="body-sm">Fix the highlighted fields to see your estimate.</Text>
              )}
            </Card>
          </Box>
        </Box>

        <Box ref={resultsRef} css={RESULTS_COLUMN_CSS}>
          <Card elevated={false} aria-live="polite" css={ESTIMATE_CARD_CSS}>
            <Heading level={3} textStyle="heading-sm">
              Your cash flow estimate
            </Heading>

            {calculation.ok ? (
              <>
                <Box css={{ marginBlock: '400' }}>
                  <Text textStyle="body-sm">Monthly cash flow</Text>
                  <Text textStyle="heading-md" css={{ display: 'block' }}>
                    {formatCurrency(calculation.result.monthlyCashFlow)}
                  </Text>

                  <Text textStyle="body-sm">Annual cash-on-cash return</Text>
                  <Text textStyle="heading-md" css={{ display: 'block' }}>
                    {calculation.result.cashOnCashReturn === undefined
                      ? 'N/A'
                      : formatPercent(calculation.result.cashOnCashReturn)}
                  </Text>
                </Box>

                <Box asChild css={{ margin: 0 }}>
                  <dl>
                    {getSummaryRows(calculation.result, purchaseMethod).map(({ label, value }) => (
                      <Box
                        key={label}
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          paddingBlock: '200',
                          borderBlockEndWidth: '1px',
                          borderBlockEndStyle: 'solid',
                          borderColor: 'border.muted',
                        }}
                      >
                        <Text textStyle="body-sm" asChild>
                          <dt>{label}</dt>
                        </Text>
                        <Text textStyle="body-sm-bold" asChild>
                          <dd>{value}</dd>
                        </Text>
                      </Box>
                    ))}
                  </dl>
                </Box>

                <Paragraph textStyle="body-sm" css={{ marginBlockStart: '400' }}>
                  {calculation.result.monthlyCashFlow >= 0
                    ? 'Your estimated income covers the costs entered. Keep a cushion for unexpected repairs and longer vacancies.'
                    : 'Your estimated costs are higher than your income. Review your rent, expenses, or financing.'}
                </Paragraph>
              </>
            ) : (
              <Paragraph textStyle="body-sm" css={{ marginBlockStart: '400' }}>
                Fix the highlighted fields to see your estimate.
              </Paragraph>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CashFlowCalculator;
